import {Box, Stack} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {forwardRef} from 'react'
import {useColorVariation} from '../../../hooks'
import useNavigation from '../../../hooks/use-navigation'
import {useMaterialVariation} from '../../../hooks/use-variation-attributes'
import GroupedColorVariationSelection from '../../../components/color-variation-selection/grouped-color-variation-selection'
import ProductVRLinks from '../../../components/product-vr-links'
import MaterialVariationSelection from '../../../components/material-variation-selection'
import {getMasterProductId} from '../../../utils/product-utils'

export const VariationSelection = ({product, isMasterLoading, onMasterChange, onVariantChange}) => {
    // we need to use the router navigation or else the navigation cannot be wrapped within a memory router
    // noinspection JSDeprecatedSymbols
    const navigate = useNavigation()

    return (
        <Stack spacing={6}>
            {/* Material selector */}
            <MaterialVariationSelection
                product={product}
                onChange={(value, href) => {
                    // skip change if no href or the current product is selected - the latter because of the onMasterChange
                    // trigger, which expects a change on the master product, but the same product would not trigger a change
                    if (!href || value === getMasterProductId(product)) return
                    navigate(href, 'replace')
                    onMasterChange?.(value, href)
                }}
            />
            {/* Color selector */}
            <GroupedColorVariationSelection
                product={product}
                loading={isMasterLoading}
                onChange={onVariantChange}
            />
        </Stack>
    )
}

VariationSelection.propTypes = {
    product: PropTypes.object,
    isMasterLoading: PropTypes.bool,
    onMasterChange: PropTypes.func,
    onVariantChange: PropTypes.func
}

// FIGMA: https://www.figma.com/file/95ObDheGIHKVlOVY8JPVCL/STOKKE-PWA?node-id=20370%3A185527
const CockpitVariations = forwardRef(
    (
        {product, isMasterLoading, onMasterChange, onVariantChange, ARProps, configurator3DLink},
        ref
    ) => {
        const colorVariation = useColorVariation(product)
        const materialVariation = useMaterialVariation(product)
        const hasVariations = colorVariation || materialVariation

        if (!hasVariations && !configurator3DLink && !ARProps?.hasAR) return null

        return (
            <Box
                __css={{
                    // extend variation container list to edge of screen as per design
                    '.swatch-group-list': {
                        paddingX: {base: 4, lg: 0},
                        marginX: {base: -4, lg: 0}
                    }
                }}
                paddingY={{base: 4, lg: 0}}
            >
                <Stack spacing={6} ref={ref}>
                    {hasVariations ? (
                        <VariationSelection
                            product={product}
                            isMasterLoading={isMasterLoading}
                            onMasterChange={onMasterChange}
                            onVariantChange={onVariantChange}
                        />
                    ) : null}
                    <ProductVRLinks
                        {...ARProps}
                        configurator3DLink={configurator3DLink}
                        product={product}
                        variant="pdp"
                    />
                </Stack>
            </Box>
        )
    }
)

CockpitVariations.displayName = 'CockpitVariations'

CockpitVariations.propTypes = {
    product: PropTypes.object,
    isMasterLoading: PropTypes.bool,
    onMasterChange: PropTypes.func,
    onVariantChange: PropTypes.func,
    ARProps: PropTypes.object,
    configurator3DLink: PropTypes.string
}

export default CockpitVariations
